import React, { useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'cccisd-axios';
import notification from 'cccisd-notification';
import Confirm from 'cccisd-confirm';
import Tooltip from 'cccisd-tooltip';
import EditAssessmentPlan from 'js/components/EditAssessmentPlan';
import style from './style.css';

import IconEdit from 'cccisd-icons/pencil5';
import IconArchive from 'cccisd-icons/drawer-in';
import IconDelete from 'cccisd-icons/bin';
import IconStop from 'cccisd-icons/cancel';

const Boilerplate = window.cccisd.boilerplate;

const CardActions = ({
    plan,
    setForceReloadQueries,
    surveyProgress,
    isCompleted,
    stoppedDate,
    planAssessmentProgress,
    thereAreCompletedSurveys,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const studentName = plan.selectedByPawn
        ? `${plan.selectedByPawn.fields.firstName} ${plan.selectedByPawn.fields.lastName}`
        : '';

    const handleArchive = async () => {
        Confirm({
            message: 'Confirm Archive',
            description: 'Are you sure you want to archive these assessments?',
            confirmLabel: 'Archive',
            abortLabel: 'Cancel',
        })
            .then(async () => {
                try {
                    const response = await axios.put(
                        Boilerplate.route('api.assignment.plan.archive', {
                            plan: plan.assignmentPlanId,
                        })
                    );

                    if (response?.data?.status === 'success') {
                        notification('Archived successfully!');
                        setForceReloadQueries();
                    } else {
                        notification({ message: 'An error occured when archiving.', type: 'danger' });
                    }
                } catch (e) {
                    console.error(e);
                }
            })
            .fail(() => {
                // nothing
            });
    };

    const handleUnarchive = async () => {
        Confirm({
            message: 'Confirm Unarchive',
            description: 'Are you sure you want to unarchive these assessments?',
            confirmLabel: 'Unarchive',
            abortLabel: 'Cancel',
        })
            .then(async () => {
                try {
                    const response = await axios.put(
                        Boilerplate.route('api.assignment.plan.unarchive', {
                            plan: plan.assignmentPlanId,
                        })
                    );

                    if (response?.data?.status === 'success') {
                        notification('Unarchived successfully!');
                        setForceReloadQueries();
                    } else {
                        notification({ message: 'An error occured when unarchiving.', type: 'danger' });
                    }
                } catch (e) {
                    console.error(e);
                }
            })
            .fail(() => {
                // nothing
            });
    };

    const handleDelete = () => {
        Confirm({
            message: 'Confirm Delete',
            description: 'Are you sure you want to delete these assessments? This action cannot be undone.',
            confirmLabel: 'Delete',
            abortLabel: 'Cancel',
        })
            .then(async () => {
                try {
                    const response = await axios.delete(
                        Boilerplate.route('api.assignment.plan.delete', {
                            plan: plan.assignmentPlanId,
                        })
                    );
                    if (response?.data?.status === 'success') {
                        notification('Assessments deleted successfully!');
                        setForceReloadQueries();
                    } else {
                        notification({ message: 'An error occurred deleting assessments', type: 'danger' });
                    }
                } catch (e) {
                    console.error(e);
                }
            })
            .fail(() => {
                // nothing
            });
    };

    let hasDuplicates = arr => new Set(arr).size !== arr.length;
    const multipleContacts = hasDuplicates(planAssessmentProgress.map(p => p.surveyHandle));

    const handleStop = () => {
        Confirm({
            message: 'Confirm Stop Assessing',
            description: (
                <div>
                    <p>Completed assessments:</p>
                    <div>
                        {planAssessmentProgress
                            .filter(pap => {
                                return pap.pawnSurveyProgress === 2;
                            })
                            .map((pap, index) => {
                                return (
                                    <ul key={pap.surveyHandle + pap.pawnDetails.pawnId} style={{ fontWeight: 'bold' }}>
                                        <li>
                                            {multipleContacts
                                                ? `${pap.surveyLabel} ${
                                                      pap.pawnDetails.name ? `(${pap.pawnDetails.name})` : ''
                                                  }`
                                                : pap.surveyLabel}
                                        </li>
                                    </ul>
                                );
                            })}
                    </div>
                    <p>Incomplete assessments:</p>
                    <div>
                        {planAssessmentProgress
                            .filter(pap => pap.pawnSurveyProgress !== 2)
                            .map((pap, index) => {
                                return (
                                    <ul key={pap.surveyHandle + pap.pawnDetails.pawnId} style={{ fontWeight: 'bold' }}>
                                        <li>
                                            {multipleContacts
                                                ? `${pap.surveyLabel} ${
                                                      pap.pawnDetails.name ? `(${pap.pawnDetails.name})` : ''
                                                  }`
                                                : pap.surveyLabel}
                                        </li>
                                    </ul>
                                );
                            })}
                    </div>
                    <p>Would you like to cancel the incomplete assessments and keep the completed assessments?</p>
                </div>
            ),
            confirmLabel: 'Stop Assessing',
            abortLabel: 'Cancel',
        })
            .then(async () => {
                try {
                    const response = await axios.put(
                        Boilerplate.route('api.assignment.plan.update', {
                            plan: plan.assignmentPlanId,
                        }),
                        {
                            settings: {
                                ...JSON.parse(plan.settings),
                                stopAssessing: new Date().toLocaleDateString(),
                            },
                        }
                    );
                    if (response?.data?.status === 'success') {
                        notification('Assessments stopped successfully!');
                        setForceReloadQueries();
                    } else {
                        notification({ message: 'An error occurred stopping assessments', type: 'danger' });
                    }
                } catch (e) {
                    console.error(e);
                }
            })
            .fail(() => {
                // nothing
            });
    };

    const renderCardActions = () => {
        if (plan.archivedDate) {
            return (
                <Tooltip title="Unarchive assessments.">
                    <button className="btn btn-info btn-sm" type="button" onClick={handleUnarchive}>
                        <IconArchive />
                    </button>
                </Tooltip>
            );
        }
        if (isCompleted || stoppedDate) {
            return (
                <Tooltip title="Move assessments to archive. This action is reversible. Use the Filter Options to view archived assessments.">
                    <button className="btn btn-warning btn-sm" type="button" onClick={handleArchive}>
                        <IconArchive />
                    </button>
                </Tooltip>
            );
        }
        return (
            <>
                {thereAreCompletedSurveys ? (
                    <Tooltip title="Cancel any assessments that have not been completed.">
                        <button className="btn btn-danger btn-sm" type="button" onClick={handleStop}>
                            <IconStop />
                        </button>
                    </Tooltip>
                ) : (
                    <>
                        <Tooltip
                            title={
                                thereAreCompletedSurveys
                                    ? 'Cannot edit once assessments have been completed'
                                    : `Add or remove assessments for ${studentName} from assignment.`
                            }
                        >
                            <button className="btn btn-success btn-sm" type="button" onClick={() => setIsEditing(true)}>
                                <IconEdit />
                            </button>
                        </Tooltip>
                        <Tooltip title="This action can only be taken if there are no assessments that have been completed.">
                            <button className="btn btn-danger btn-sm" type="button" onClick={handleDelete}>
                                <IconDelete />
                            </button>
                        </Tooltip>
                    </>
                )}
            </>
        );
    };

    return (
        <>
            <>
                {isEditing && (
                    <EditAssessmentPlan
                        planId={plan.assignmentPlanId}
                        pawnId={plan.selectedByPawn.pawn.pawnId}
                        type="modify"
                        setIsEditing={setIsEditing}
                        setForceReloadQueries={setForceReloadQueries}
                        title={`Edit Assessments for ${studentName}`}
                    />
                )}
            </>
            <div className={style.cardActions}>{renderCardActions()}</div>
        </>
    );
};

export default CardActions;

CardActions.propTypes = {
    plan: PropTypes.object,
    setForceReloadQueries: PropTypes.func,
    surveyProgress: PropTypes.object,
};

CardActions.defaultProps = {};
